import React, { useState, useEffect } from 'react'
import { Formik, Form } from 'formik'
import { useNavigate, useParams } from 'react-router-dom'

import { useOrderedGetGalleryQuery } from 'shared/extended'
import FormField from 'src/components/FormField'
import { FormFieldWrapper } from 'src/components/FormField/styled'
import {
	GalleriesGetAllDocument,
	GetGalleryDocument,
	useAddGalleryPhotosMutation,
	useCreateGalleryMutation,
	useUpdateGalleryMutation,
	useGetGalleryLazyQuery,
	useUpdateGalleryPhotoOrderMutation,
	useRemoveGalleryPhotoMutation
} from 'shared/schema'
import _ from 'lodash'
import FormLabel from 'src/components/FormLabel'
import { Delete, GalleryButtonsContainer, GalleryPhotoCard, GalleryPhotoImage } from '../styled'
import { useUserRoles } from 'src/components/Roles'
import { useUpload } from 'src/components/Upload'
import { Image, CameraVideo } from 'src/icons'
import styled from 'styled-components'

const ToggleButton = styled.button<{ $active: boolean }>`
	flex: 1;
	border: none;
	border-radius: 3px;
	padding: 0.5rem 1rem;
	font-family: inherit;
	font-size: inherit;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	font-weight: 400;
	transition: all 0.2s ease;
	background: ${(props) => (props.$active ? props.theme.colors.primary : 'transparent')};
	color: ${(props) => (props.$active ? '#fff' : props.theme.colors.primaryTextColor)};
	cursor: pointer;
	&:hover:not(:disabled) {
		background: ${(props) => (props.$active ? props.theme.colors.primaryDark : props.theme.colors.lightGray)};
	}
	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`

const processImageBase =
	location.host === 'localhost:1234'
		? 'https://onenibbi-v2-backend.vercel.app/api/processImage?uploadId='
		: 'https://onenibbi-v2-backend.vercel.app/api/processImage?uploadId='

// Upload status types
const UPLOAD_STATUS = {
	PENDING: 'pending',
	UPLOADING: 'uploading',
	PROCESSING: 'processing',
	SUCCESS: 'success',
	ERROR: 'error'
}

// Error component
const ErrorMessage = ({ error, onRetry, onDismiss }) => (
	<div style={{
		padding: '8px 12px',
		backgroundColor: '#fee',
		border: '1px solid #fcc',
		borderRadius: '4px',
		marginBottom: '8px',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center'
	}}>
		<span style={{ color: '#c00', fontSize: '14px' }}>{error}</span>
		<div>
			{onRetry && (
				<button
					type="button"
					onClick={onRetry}
					style={{
						marginLeft: '8px',
						padding: '4px 8px',
						fontSize: '12px',
						cursor: 'pointer'
					}}
				>
					Retry
				</button>
			)}
			<button
				type="button"
				onClick={onDismiss}
				style={{
					marginLeft: '8px',
					padding: '4px 8px',
					fontSize: '12px',
					cursor: 'pointer'
				}}
			>
				Dismiss
			</button>
		</div>
	</div>
)

// Progress bar component
const ProgressBar = ({ progress, status }) => {
	const getStatusColor = () => {
		switch (status) {
			case UPLOAD_STATUS.ERROR: return '#dc3545'
			case UPLOAD_STATUS.SUCCESS: return '#28a745'
			case UPLOAD_STATUS.PROCESSING: return '#ffc107'
			default: return '#007bff'
		}
	}

	return (
		<div style={{
			width: '100%',
			height: '4px',
			backgroundColor: '#e9ecef',
			borderRadius: '2px',
			overflow: 'hidden',
			marginTop: '4px'
		}}>
			<div
				style={{
					width: `${progress}%`,
					height: '100%',
					backgroundColor: getStatusColor(),
					transition: 'width 0.3s ease, background-color 0.3s ease'
				}}
			/>
		</div>
	)
}

// Single file progress card
const UploadProgressCard = ({ progress }) => (
	<div style={{
		padding: '8px',
		marginBottom: '8px',
		backgroundColor: '#f8f9fa',
		borderRadius: '4px',
		border: '1px solid #dee2e6'
	}}>
		<div style={{
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			marginBottom: '4px'
		}}>
			<span style={{ fontSize: '14px', fontWeight: '500' }}>
				{progress.fileName}
			</span>
			<span style={{
				fontSize: '12px',
				color: progress.status === UPLOAD_STATUS.ERROR ? '#dc3545' : '#6c757d'
			}}>
				{progress.status === UPLOAD_STATUS.ERROR ? 'Failed' :
				 progress.status === UPLOAD_STATUS.SUCCESS ? 'Complete' :
				 progress.status === UPLOAD_STATUS.PROCESSING ? 'Processing...' :
				 progress.status === UPLOAD_STATUS.UPLOADING ? 'Uploading...' : 'Pending'}
			</span>
		</div>
		<ProgressBar progress={progress.progress} status={progress.status} />
		{progress.error && (
			<div style={{ fontSize: '12px', color: '#dc3545', marginTop: '4px' }}>
				{progress.error}
			</div>
		)}
	</div>
)

// Upload status display component
const UploadStatusDisplay = ({ uploads }) => {
	const totalFiles = Object.keys(uploads).length
	const completedFiles = Object.values(uploads).filter(u => u.status === UPLOAD_STATUS.SUCCESS).length
	const errorFiles = Object.values(uploads).filter(u => u.status === UPLOAD_STATUS.ERROR).length
	const uploadingFiles = Object.values(uploads).filter(u => 
		u.status === UPLOAD_STATUS.UPLOADING || u.status === UPLOAD_STATUS.PROCESSING
	).length

	if (totalFiles === 0) return null

	return (
		<div style={{
			padding: '12px',
			backgroundColor: '#f8f9fa',
			borderRadius: '4px',
			marginBottom: '16px'
		}}>
			<div style={{ marginBottom: '8px', fontWeight: 'bold' }}>
				Upload Progress: {completedFiles}/{totalFiles} files
			</div>
			{uploadingFiles > 0 && (
				<div style={{ fontSize: '14px', color: '#666' }}>
					Uploading {uploadingFiles} file{uploadingFiles > 1 ? 's' : ''}...
				</div>
			)}
			{errorFiles > 0 && (
				<div style={{ fontSize: '14px', color: '#dc3545', marginTop: '4px' }}>
					{errorFiles} file{errorFiles > 1 ? 's' : ''} failed to upload
				</div>
			)}
		</div>
	)
}

function GalleryForm() {
	const { galleryId } = useParams()
	const navigate = useNavigate()
	const mutationConfig = {
		refetchQueries: [
			{ query: GalleriesGetAllDocument },
			{ query: GetGalleryDocument, variables: { id: +galleryId > 0 ? +galleryId : -1 } }
		],
		awaitRefetchQueries: true
	}

	const [updateGallery] = useUpdateGalleryMutation(mutationConfig)
	const [updateGalleryOrder] = useUpdateGalleryPhotoOrderMutation(mutationConfig)
	const [createGallery] = useCreateGalleryMutation(mutationConfig)
	const [getGallery, { data: postUpdateGalleryData }] = useGetGalleryLazyQuery()

	const [uploading, setUploading] = useState(false)
	const [newPhotos, setNewPhotos] = useState<{ [fileKey: string]: { uploadId: number; publicUrl: string } }>({})
	const [uploadProgress, setUploadProgress] = useState({})
	const [uploadErrors, setUploadErrors] = useState([])
	const { data, loading } = useOrderedGetGalleryQuery(+galleryId, !galleryId)
	const [upload] = useUpload(true)
	const [addGalleryPhotos] = useAddGalleryPhotosMutation()
	const [removeGalleryPhoto] = useRemoveGalleryPhotoMutation(mutationConfig)

	// Media type selector (photos or video, mutually exclusive)
	const [mediaType, setMediaType] = useState<'photos' | 'video'>('photos')

	// Video upload state
	const [videoUpload, setVideoUpload] = useState<{ uploadId: number; publicUrl: string } | null>(null)
	const [videoThumbnailUpload, setVideoThumbnailUpload] = useState<{ uploadId: number; publicUrl: string } | null>(null)
	const [videoUploading, setVideoUploading] = useState(false)
	const [thumbnailUploading, setThumbnailUploading] = useState(false)

	// Pre-fill video state from existing gallery data
	const galleryVideoUploadId = (data?.gallery as any)?.videoUpload?.id
	const galleryVideoThumbnailId = (data?.gallery as any)?.videoThumbnail?.id
	useEffect(() => {
		if (data?.gallery) {
			const gallery = data.gallery as any
			if (gallery.videoUpload && !videoUpload) {
				setVideoUpload({ uploadId: gallery.videoUpload.id, publicUrl: gallery.videoUpload.publicUrl })
				setMediaType('video')
			}
			if (gallery.videoThumbnail && !videoThumbnailUpload) {
				setVideoThumbnailUpload({ uploadId: gallery.videoThumbnail.id, publicUrl: gallery.videoThumbnail.publicUrl })
			}
		}
	}, [galleryVideoUploadId, galleryVideoThumbnailId])

	const { isGalleryAdmin } = useUserRoles()

	useEffect(() => {
		if (!isGalleryAdmin) {
			navigate('/gallery')
		}
	}, [isGalleryAdmin])

	useEffect(() => {
		const check = async () => {
			if (postUpdateGalleryData) {
				if (
					(postUpdateGalleryData.gallery.children as any).length !=
					postUpdateGalleryData.gallery.photos.length
				) {
					await updateGalleryOrder({
						variables: {
							id: postUpdateGalleryData.gallery.id,
							children: postUpdateGalleryData.gallery.photos.map((p) => p.id)
						}
					})
				}
				navigate('/gallery')
			}
		}
		check()
	}, [postUpdateGalleryData])

	if (loading) {
		return null
	}

	const prefillFormData = {
		title: data?.gallery?.title || '',
		date: data?.gallery?.date?.split('T')[0] || '',
		photos: data?.gallery?.photos || []
	}

	// Helper function to update upload progress
	const updateUploadProgress = (fileKey, updates) => {
		setUploadProgress(prev => ({
			...prev,
			[fileKey]: { ...prev[fileKey], ...updates }
		}))
	}

	// Helper function to handle single file upload with retry capability
	const uploadSingleFile = async (file, fileKey, retryCount = 0) => {
		const MAX_RETRIES = 1
		
		try {
			// Update status to uploading
			updateUploadProgress(fileKey, {
				status: UPLOAD_STATUS.UPLOADING,
				progress: 0,
				fileName: file.name,
				fileSize: file.size,
				error: null
			})

			// Simulate progress updates (you might need to modify the upload hook to support progress)
			const progressInterval = setInterval(() => {
				setUploadProgress(prev => {
					const current = prev[fileKey]
					if (current && current.progress < 90 && current.status === UPLOAD_STATUS.UPLOADING) {
						return {
							...prev,
							[fileKey]: { ...current, progress: current.progress + 10 }
						}
					}
					return prev
				})
			}, 200)

			// Upload file
			const uploadResult = await upload(file)
			clearInterval(progressInterval)

			// Update status to processing
			updateUploadProgress(fileKey, {
				status: UPLOAD_STATUS.PROCESSING,
				progress: 95
			})

			// Process image
			// const processImageRequest = await fetch(processImageBase + uploadResult.uploadId)
			
			// if (!processImageRequest.ok) {
			// 	throw new Error(`Image processing failed: ${processImageRequest.statusText}`)
			// }

			// const processImageResult = await processImageRequest.json()
			
			// Update status to success
			updateUploadProgress(fileKey, {
				status: UPLOAD_STATUS.SUCCESS,
				progress: 100
			})

			return {
				uploadId: uploadResult.uploadId,
				publicUrl: uploadResult.publicUrl
			}
		} catch (error) {
			console.error(`Upload failed for ${file.name}:`, error)
			
			if (retryCount < MAX_RETRIES) {
				// Retry upload
				updateUploadProgress(fileKey, {
					status: UPLOAD_STATUS.UPLOADING,
					progress: 0,
					error: `Retrying... (${retryCount + 1}/${MAX_RETRIES})`
				})
				
				// Wait before retry
				await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)))
				return uploadSingleFile(file, fileKey, retryCount + 1)
			}
			
			// Update status to error
			updateUploadProgress(fileKey, {
				status: UPLOAD_STATUS.ERROR,
				progress: 0,
				error: error.message || 'Upload failed'
			})
			
			throw error
		}
	}

	// Handle file selection and upload
	const handleFileUpload = async (ev) => {
		ev.persist()
		setUploading(true)
		setUploadErrors([])

		const files = Array.from(ev.target.files)
		const newUploads = files
			.map((file) => ({ file, key: `${file.name}:${file.size}` }))
			.filter((fileEntry) => !newPhotos[fileEntry.key])

		// Initialize upload progress for all files
		const initialProgress = {}
		newUploads.forEach(({ key, file }) => {
			initialProgress[key] = {
				status: UPLOAD_STATUS.PENDING,
				progress: 0,
				fileName: file.name,
				fileSize: file.size,
				error: null
			}
		})
		setUploadProgress(prev => ({ ...prev, ...initialProgress }))

		// Upload files concurrently with a limit
		const CONCURRENT_LIMIT = 3
		const uploadResults = {}
		const errors = []

		for (let i = 0; i < newUploads.length; i += CONCURRENT_LIMIT) {
			const batch = newUploads.slice(i, i + CONCURRENT_LIMIT)
			const batchPromises = batch.map(async ({ file, key }) => {
				try {
					const result = await uploadSingleFile(file, key)
					uploadResults[key] = result
				} catch (error) {
					errors.push({
						fileName: file.name,
						error: error.message || 'Upload failed',
						key
					})
				}
			})

			await Promise.all(batchPromises)
		}

		// Update state with successful uploads
		if (Object.keys(uploadResults).length > 0) {
			setNewPhotos(prev => ({ ...prev, ...uploadResults }))
		}

		// Set errors if any
		if (errors.length > 0) {
			setUploadErrors(errors)
		}

		setUploading(false)
		ev.target.value = ''
	}

	// Retry failed upload
	const retryFailedUpload = async (fileKey) => {
		const progressInfo = uploadProgress[fileKey]
		if (!progressInfo) return

		// Create a file object from the stored info (you might need to store the actual file)
		// For now, this is a placeholder - you'd need to modify the initial upload to store files
		console.log('Retry functionality needs file reference implementation')
	}

	const handleVideoUpload = async (ev) => {
		const file = ev.target.files?.[0]
		if (!file) return
		const allowedVideoTypes = ['video/mp4', 'video/quicktime']
		if (!allowedVideoTypes.includes(file.type)) {
			setUploadErrors(prev => [...prev, { error: 'Only MP4 and MOV videos are supported', key: 'video-type' }])
			ev.target.value = ''
			return
		}
		if (file.size > 500 * 1024 * 1024) {
			setUploadErrors(prev => [...prev, { error: 'Video must be under 500MB', key: 'video-size' }])
			ev.target.value = ''
			return
		}
		const fileKey = `video:${file.name}:${file.size}`
		setVideoUploading(true)
		try {
			const result = await uploadSingleFile(file, fileKey)
			setVideoUpload({ uploadId: result.uploadId, publicUrl: result.publicUrl })
		} catch (error) {
			setUploadErrors(prev => [...prev, { error: `Video upload failed: ${error.message}`, key: 'video-upload' }])
		}
		setVideoUploading(false)
		ev.target.value = ''
	}

	const handleThumbnailUpload = async (ev) => {
		const file = ev.target.files?.[0]
		if (!file) return
		const fileKey = `thumbnail:${file.name}:${file.size}`
		setThumbnailUploading(true)
		try {
			const result = await uploadSingleFile(file, fileKey)
			setVideoThumbnailUpload({ uploadId: result.uploadId, publicUrl: result.publicUrl })
		} catch (error) {
			setUploadErrors(prev => [...prev, { error: `Thumbnail upload failed: ${error.message}`, key: 'thumb-upload' }])
		}
		setThumbnailUploading(false)
		ev.target.value = ''
	}

	return (
		<Formik
			initialValues={prefillFormData}
			onSubmit={async (values) => {
				console.log('[VideoUpload] Form submit - videoUpload:', videoUpload, 'videoThumbnailUpload:', videoThumbnailUpload)
				const getDate = () => {
					if (values.date?.length === 0) {
						return new Date().toISOString().substring(0, 10)
					} else {
						return values.date
					}
				}
				let newGalleryId = +galleryId
				
				try {
					const submitVideoId = mediaType === 'video' ? (videoUpload?.uploadId || null) : null
					const submitThumbnailId = mediaType === 'video' ? (videoThumbnailUpload?.uploadId || null) : null
					const submitPhotos = mediaType === 'photos' ? Object.values(newPhotos) : []

					if (galleryId) {
						if (submitPhotos.length > 0) {
							await addGalleryPhotos({
								variables: {
									objects: submitPhotos.map((newPhoto) => ({
										caption: '',
										galleryId,
										imageUploadId: newPhoto.uploadId
									}))
								}
							})
						}
						await updateGallery({
							variables: {
								id: +galleryId,
								title: values.title,
								date: getDate(),
								videoUploadId: submitVideoId,
								videoThumbnailUploadId: submitThumbnailId
							}
						})
					} else {
						const result = await createGallery({
							variables: {
								title: values.title,
								date: getDate(),
								photos: {
									data: submitPhotos.map((newPhoto) => ({
										caption: '',
										galleryId,
										imageUploadId: newPhoto.uploadId
									}))
								},
								videoUploadId: submitVideoId,
								videoThumbnailUploadId: submitThumbnailId
							}
						})
						newGalleryId = result.data.insertGallery.id
					}
					await getGallery({ variables: { id: newGalleryId } })
				} catch (error) {
					console.error('Failed to save gallery:', error)
					setUploadErrors([{
						error: 'Failed to save gallery. Please try again.',
						key: 'gallery-save'
					}])
				}
			}}
		>
			<Form>
				<FormField name="title" type="text" label="Title" placeholder="Gallery Title" />
				<FormField name="date" type="date" label="Date" />

				{/* Media type selector */}
				<FormFieldWrapper>
					<FormLabel>Media type</FormLabel>
					<div style={{
						display: 'flex',
						borderRadius: '3px',
						border: '1px solid #DEDEDE',
						padding: '4px',
						gap: '4px',
						background: '#fff',
					}}>
						<ToggleButton
							type="button"
							$active={mediaType === 'photos'}
							onClick={() => setMediaType('photos')}
							disabled={uploading || videoUploading || thumbnailUploading}
						>
							<Image style={{ width: 16, height: 16 }} />
							Photos
						</ToggleButton>
						<ToggleButton
							type="button"
							$active={mediaType === 'video'}
							onClick={() => setMediaType('video')}
							disabled={uploading || videoUploading || thumbnailUploading}
						>
							<CameraVideo style={{ width: 16, height: 16 }} />
							Video
						</ToggleButton>
					</div>
				</FormFieldWrapper>

				{/* Error messages */}
				{uploadErrors.map((error, index) => (
					<ErrorMessage
						key={error.key || index}
						error={`${error.fileName ? `${error.fileName}: ` : ''}${error.error}`}
						onRetry={error.key && error.key !== 'gallery-save' ? () => retryFailedUpload(error.key) : null}
						onDismiss={() => setUploadErrors(prev => prev.filter((_, i) => i !== index))}
					/>
				))}

				{/* Video upload section — only shown when "Video" is selected */}
				{mediaType === 'video' && (
					<>
						<FormField
							name="video"
							type="file"
							label="Video (MP4/MOV, max 500MB)"
							buttonLabel={videoUpload ? 'Replace Video' : 'Upload Video'}
							disabled={videoUploading}
							onChange={handleVideoUpload}
						/>

						{/* Video/thumbnail upload progress */}
						{Object.entries(uploadProgress)
							.filter(([key]) => key.startsWith('video:') || key.startsWith('thumbnail:'))
							.map(([key, progress]) => (
								<UploadProgressCard key={key} progress={progress} />
							))
						}

						{videoUpload && (
							<>
								<FormLabel>Video</FormLabel>
								<div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gridGap: 8, padding: 8, marginBottom: '16px' }}>
									<GalleryPhotoCard style={{ position: 'relative' }}>
										<GalleryPhotoImage style={{
											backgroundImage: videoThumbnailUpload ? `url(${videoThumbnailUpload.publicUrl})` : undefined,
											backgroundColor: !videoThumbnailUpload ? '#e9ecef' : undefined,
											display: 'flex', alignItems: 'center', justifyContent: 'center'
										}}>
											{!videoThumbnailUpload && (
												<span style={{ color: '#6c757d', fontSize: '12px', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>No thumbnail</span>
											)}
										</GalleryPhotoImage>
										{/* Green checkmark */}
										<div style={{
											position: 'absolute',
											top: '4px',
											right: '4px',
											backgroundColor: '#28a745',
											color: 'white',
											borderRadius: '50%',
											width: '20px',
											height: '20px',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											fontSize: '12px',
											zIndex: 3
										}}>
											✓
										</div>
										{/* Play icon overlay */}
										<div style={{
											position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
											display: 'flex', alignItems: 'center', justifyContent: 'center',
											pointerEvents: 'none'
										}}>
											<div style={{
												width: 36, height: 36, borderRadius: '50%', backgroundColor: 'rgba(0,0,0,0.5)',
												display: 'flex', alignItems: 'center', justifyContent: 'center'
											}}>
												<div style={{ width: 0, height: 0, borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderLeft: '14px solid #fff', marginLeft: '3px' }} />
											</div>
										</div>
										<GalleryButtonsContainer>
											<Delete
												onClick={() => {
													if (confirm('Remove video from this gallery?')) {
														setVideoUpload(null)
														setVideoThumbnailUpload(null)
													}
												}}
											/>
										</GalleryButtonsContainer>
									</GalleryPhotoCard>
								</div>
							</>
						)}

						<FormField
							name="videoThumbnail"
							type="file"
							label={videoThumbnailUpload ? 'Replace video thumbnail' : 'Video thumbnail (required)'}
							buttonLabel={videoThumbnailUpload ? 'Replace Thumbnail' : 'Upload Thumbnail'}
							disabled={thumbnailUploading}
							onChange={handleThumbnailUpload}
						/>
						{videoThumbnailUpload && (
							<div style={{ marginTop: '8px' }}>
								<FormLabel>Current thumbnail</FormLabel>
								<img
									src={videoThumbnailUpload.publicUrl}
									alt="Video thumbnail"
									style={{
										maxWidth: '200px',
										borderRadius: '3px',
										border: '1px solid #DEDEDE'
									}}
								/>
							</div>
						)}
					</>
				)}

				{/* Photo upload section — only shown when "Photos" is selected */}
				{mediaType === 'photos' && (
					<>
						<UploadStatusDisplay uploads={Object.fromEntries(Object.entries(uploadProgress).filter(([key]) => !key.startsWith('video:') && !key.startsWith('thumbnail:')))} />

						<FormField
							name="photo"
							type="file"
							label="Upload photo(s)"
							multiple
							disabled={uploading}
							onChange={handleFileUpload}
						/>

						{/* Display uploading files with progress */}
						{Object.entries(uploadProgress).filter(([key]) => !key.startsWith('video:') && !key.startsWith('thumbnail:')).length > 0 && (
							<>
								<FormLabel>Upload Progress</FormLabel>
								<div style={{ marginBottom: '16px' }}>
									{Object.entries(uploadProgress).filter(([key]) => !key.startsWith('video:') && !key.startsWith('thumbnail:')).map(([key, progress]) => (
										<div key={key} style={{
											padding: '8px',
											marginBottom: '8px',
											backgroundColor: '#f8f9fa',
											borderRadius: '4px',
											border: '1px solid #dee2e6'
										}}>
											<div style={{
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'center',
												marginBottom: '4px'
											}}>
												<span style={{ fontSize: '14px', fontWeight: '500' }}>
													{progress.fileName}
												</span>
												<span style={{
													fontSize: '12px',
													color: progress.status === UPLOAD_STATUS.ERROR ? '#dc3545' : '#6c757d'
												}}>
													{progress.status === UPLOAD_STATUS.ERROR ? 'Failed' :
													 progress.status === UPLOAD_STATUS.SUCCESS ? 'Complete' :
													 progress.status === UPLOAD_STATUS.PROCESSING ? 'Processing...' :
													 progress.status === UPLOAD_STATUS.UPLOADING ? 'Uploading...' : 'Pending'}
												</span>
											</div>
											<ProgressBar progress={progress.progress} status={progress.status} />
											{progress.error && (
												<div style={{ fontSize: '12px', color: '#dc3545', marginTop: '4px' }}>
													{progress.error}
												</div>
											)}
										</div>
									))}
								</div>
							</>
						)}

						{/* Existing photos (from saved gallery) */}
						{data?.gallery?.photos?.length > 0 && (
							<>
								<FormLabel>Existing photo(s)</FormLabel>
								<div
									style={{
										display: 'grid',
										gridTemplateColumns: `repeat(5, 1fr)`,
										gridGap: 8,
										padding: 8
									}}
								>
									{data.gallery.photos.map((photo) => (
										<GalleryPhotoCard key={photo.id} style={{ position: 'relative' }}>
											<GalleryPhotoImage
												style={{
													backgroundImage: `url(${photo.image?.publicUrl})`
												}}
											/>
											<GalleryButtonsContainer>
												<Delete
													onClick={async () => {
														if (confirm('Are you sure you want to delete this photo?')) {
															await removeGalleryPhoto({
																variables: { id: photo.id }
															})
														}
													}}
												/>
											</GalleryButtonsContainer>
										</GalleryPhotoCard>
									))}
								</div>
							</>
						)}

						{/* Successfully uploaded photos */}
						{Object.keys(newPhotos).length > 0 && (
							<>
								<FormLabel>New photo(s)</FormLabel>
								<div
									style={{
										display: 'grid',
										gridTemplateColumns: `repeat(5, 1fr)`,
										gridGap: 8,
										padding: 8
									}}
								>
									{Object.entries(newPhotos).map(([pKey, p]) => {
										const progress = uploadProgress[pKey]
										return (
											<GalleryPhotoCard key={p.uploadId} style={{
												position: 'relative',
												opacity: progress?.status === UPLOAD_STATUS.SUCCESS ? 1 : 0.8
											}}>
												<GalleryPhotoImage
													style={{
														backgroundImage: `url(${p.publicUrl})`
													}}
												/>
												{progress?.status === UPLOAD_STATUS.SUCCESS && (
													<div style={{
														position: 'absolute',
														top: '4px',
														right: '4px',
														backgroundColor: '#28a745',
														color: 'white',
														borderRadius: '50%',
														width: '20px',
														height: '20px',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														fontSize: '12px'
													}}>
														✓
													</div>
												)}
												<GalleryButtonsContainer>
													<Delete
														onClick={() => {
															if (confirm('Are you sure you want to delete this photo?')) {
																setNewPhotos(_.omit(newPhotos, pKey))
																setUploadProgress(_.omit(uploadProgress, pKey))
															}
														}}
													/>
												</GalleryButtonsContainer>
											</GalleryPhotoCard>
										)
									})}
								</div>
							</>
						)}
					</>
				)}

				<FormField
					type="formButtons"
					isLoading={uploading || videoUploading || thumbnailUploading}
					onCancel={galleryId ? '../..' : '..'}
				/>
			</Form>
		</Formik>
	)
}

export default GalleryForm